import { CmsPage, CmsPageSection, GlobalBlock } from '../../models/content';

/**
 * Global blocks (#217; Conductor/task-global-blocks.md): a named run of sections defined once at
 * content.globalBlocks.<id> and placed on a page by a section `{ component: 'global-block', globalBlock: <id> }`. The
 * site draws the global block's sections in place of the reference, so one edit reaches every page that places it.
 * conductor-server enforces the same rules (api/classes/website/global-blocks.ts). Pure; tested in
 * global-blocks.spec.ts.
 */

export const GLOBAL_BLOCK_COMPONENT = 'global-block';

/** The reference, the site's snapshot id and the publish token id: must fit the scope grammar, and never changes. */
export function newGlobalBlockId(): string {
  return `id-gb-${Math.random().toString(36).slice(2, 10)}`;
}

export const isReference = (s: Pick<CmsPageSection, 'component'> | null | undefined): boolean =>
  s?.component === GLOBAL_BLOCK_COMPONENT;

/** The page headline: a global block can sit on any page, so it never holds one. */
export const isHero = (s: Pick<CmsPageSection, 'type' | 'component'>): boolean =>
  s.type === 'editorial-hero' || s.component === 'service-hero';

/** The account stream hands collections back as arrays, the store as id-keyed maps: read either. */
const listOf = <T>(raw: unknown): T[] =>
  (Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.values(raw) : []).filter(
    (x: any) => x && typeof x === 'object' && x?._meta?.deleted !== true
  ) as T[];

const indexOf = (s: any) => (typeof s?._meta?.index === 'number' ? s._meta.index : Number.MAX_SAFE_INTEGER);

/** Every live global block in the website content, by name. */
export function globalBlocksIn(content: any): GlobalBlock[] {
  return listOf<GlobalBlock>(content?.globalBlocks).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/** A global block's (or a page's) sections in the order the site draws them. */
export function sectionsOf(holder: { sections?: unknown } | null | undefined): CmsPageSection[] {
  return listOf<CmsPageSection>(holder?.sections).sort((a, b) => indexOf(a) - indexOf(b));
}

/** One place a global block is used, named as an author recognises it. */
export interface GlobalBlockUse {
  label: string;
  kind: 'page' | 'template' | 'suburb';
}

const placesIt = (collection: unknown, id: string) =>
  listOf<CmsPageSection>(collection).some((s) => isReference(s) && s.globalBlock === id);

/**
 * Everywhere a global block is placed, as the server counts uses for its delete check (api/classes/website/
 * global-blocks.ts): a page's sections (published or not), a Suburb or Sub-region template's live blocks, a suburb's
 * inserted blocks.
 */
export function globalBlockUses(content: any, id: string): GlobalBlockUse[] {
  const pages = listOf<CmsPage>(content?.pages)
    .filter((p) => placesIt(p.sections, id))
    .sort((a, b) => (a.path || '').localeCompare(b.path || ''))
    .map((p) => ({ label: `${p.title || '(untitled)'} (${p.path})`, kind: 'page' as const }));
  const templates = listOf<any>(content?.pageTemplates)
    .filter((t) => placesIt(t.blocks, id))
    .map((t) => ({ label: `the ${t.key === 'subregion' ? 'Sub-region' : 'Suburb'} template`, kind: 'template' as const }));
  const suburbs = listOf<any>(content?.suburbs)
    .filter((sb) => placesIt(sb.blocks, id))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    .map((sb) => ({ label: `the ${sb.name || sb.slug} page`, kind: 'suburb' as const }));
  return [...pages, ...templates, ...suburbs];
}

/**
 * The publish tokens of the global blocks a set of blocks places (Ivan, 2026-11-04): publishing a page, a suburb page or
 * a template stages its global blocks too, so a newly placed one shows on the preview. The promote dialog still lists
 * each as its own row, so none goes live unreviewed.
 */
export function globalBlockTokens(...collections: unknown[]): string[] {
  const ids = new Set<string>();
  for (const c of collections) {
    for (const s of listOf<CmsPageSection>(c)) if (isReference(s) && s.globalBlock) ids.add(s.globalBlock);
  }
  return [...ids].sort().map((id) => `globalBlocks:${id}`);
}

/** The publishing name the server keeps unique (its contentKey; picaluna-v3's slugify). */
export function nameKey(name: string | null | undefined): string {
  return String(name ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

/** What stops a global block saving; null when it can. Mirrors the server's rules, worded for the editor. */
export function globalBlockProblem(
  name: string,
  sections: Array<Pick<CmsPageSection, 'type' | 'component'>>,
  others: GlobalBlock[]
): string | null {
  if (!nameKey(name)) return 'Give the global block a name. It is how you pick it on a page.';
  if (others.some((g) => nameKey(g.name) === nameKey(name))) {
    return `Another global block is already called "${name.trim()}". Give this one a different name, so the two can be told apart when you pick one on a page.`;
  }
  if (!sections.length) return 'Add at least one block. A global block with nothing in it would show nothing on every page that uses it.';
  if (sections.some(isHero)) return "A global block can't hold a hero: it can sit on any page, and a page has exactly one headline.";
  if (sections.some(isReference)) return "A global block can't contain another global block.";
  return null;
}
